import React, { useEffect, useState, useCallback } from "react";
import { GraduationCap, X, Mail, MailX, MailCheck } from "lucide-react";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

/**
 * Unread coach broadcasts surfaced on the student's /dashboard.
 * Student can dismiss (mark-read) and follow-up tap reveals expanded body.
 * Also exposes a 1-click email-opt-out toggle for these messages.
 */
export default function CoachMessagesBanner() {
  const { user, refreshMe } = useAuth();
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState({});
  // Default to opted-in if undefined (backend treats undefined as opt-in too).
  const optedIn = user?.coach_email_opt_in !== false;

  const load = useCallback(() => {
    api.get("/me/coach-messages?unread_only=true&limit=10")
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]));
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    load();
    const t = setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return;
      load();
    }, 60 * 1000);
    return () => clearInterval(t);
  }, [load, user]);

  const dismiss = async (id) => {
    try {
      await api.post(`/me/coach-messages/${id}/read`);
      setItems((arr) => arr.filter((m) => m.id !== id));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const toggleEmail = async () => {
    try {
      await api.patch("/me/preferences/coach-email", { coach_email_opt_in: !optedIn });
      toast.success(!optedIn ? "Coach emails ON" : "Coach emails muted — you'll still see them in-app");
      refreshMe?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  if (!user) return null;
  if (items.length === 0) return null;

  return (
    <section className="mt-6" data-testid="coach-messages-banner">
      <div className="text-xs text-zinc-500 uppercase tracking-wider mb-2 flex items-center justify-between gap-2">
        <span className="flex items-center gap-2">
          <Mail size={11} className="text-[#00E676]" /> {items.length} new message{items.length === 1 ? "" : "s"} from your coach
        </span>
        <button onClick={toggleEmail} data-testid="coach-msg-email-toggle"
          className="text-[10px] flex items-center gap-1 hover:text-white normal-case">
          {optedIn ? <><MailCheck size={11} /> Email on</> : <><MailX size={11} /> Email muted</>}
        </button>
      </div>
      <div className="space-y-2" data-testid="coach-messages-list">
        {items.map((m) => {
          const isExp = expanded[m.id];
          const preview = (m.body || "").length > 140 && !isExp
            ? m.body.slice(0, 140) + "…"
            : m.body;
          return (
            <div key={m.id} data-testid={`coach-msg-${m.id}`}
              className="pp-card-elevated p-4 border-[#00E676]/30 shadow-[0_8px_24px_rgba(0,230,118,0.08)]">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <Avatar className="h-9 w-9 ring-2 ring-[#00E676]/40">
                    <AvatarFallback className="bg-[#00E676]/15 text-[#00E676]"><GraduationCap size={16} /></AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-[#00E676]">
                      {m.coach_name || "Coach"} · {m.created_at ? new Date(m.created_at).toLocaleString(undefined, { weekday: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                    </div>
                    {m.title && <div className="font-bold text-sm mt-0.5">{m.title}</div>}
                    <div className="text-sm text-zinc-200 mt-1 whitespace-pre-wrap break-words" data-testid={`coach-msg-body-${m.id}`}>{preview}</div>
                    {(m.body || "").length > 140 && (
                      <button onClick={() => setExpanded((s) => ({ ...s, [m.id]: !s[m.id] }))}
                        data-testid={`coach-msg-toggle-${m.id}`}
                        className="text-[11px] text-[#00E676] hover:underline mt-1">
                        {isExp ? "Show less" : "Read more"}
                      </button>
                    )}
                  </div>
                </div>
                <button onClick={() => dismiss(m.id)} aria-label="Mark read"
                  data-testid={`coach-msg-dismiss-${m.id}`}
                  className="p-1 text-zinc-500 hover:text-white shrink-0"><X size={14} /></button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
